import { createEventHook } from '@vueuse/core';
import { computed, toValue, type MaybeRefOrGetter, type ShallowRef } from 'vue';

import type { Message } from '@/shared/api';

const useMessagesListIntersectionObserver = <
  T extends Record<any, any> & { id: Message['id']; createdAt: Date },
>(
  _data: MaybeRefOrGetter<T[]>,
  element: ShallowRef<HTMLElement | null>,
) => {
  const ATTRIBUTE_NAME: string = 'data-message-id';

  const { on: onObserver, trigger: triggerObserver } = createEventHook<{ item: T }>();

  let observer: IntersectionObserver | null = null;

  const data = computed<T[]>(() => toValue(_data));
  const dataMapById = computed<Map<T['id'], T>>(() => new Map(data.value.map((el) => [el.id, el])));

  let pendingData: T[] = [];
  let timeout: number | null = null;

  const getItemAttributes = (item: T) => ({ [ATTRIBUTE_NAME]: item.id });

  const setItemRef = (el: HTMLElement) => {
    observer?.observe(el);
  };

  const checkTarget = (target: Element) => {
    const itemId = target.getAttribute(ATTRIBUTE_NAME);
    const item = itemId ? dataMapById.value.get(itemId) : null;
    if (item) {
      pendingData.push(item);

      observer?.unobserve(target);
    }
  };

  const checkAndProcessPending = () => {
    if (pendingData.length > 0) {
      if (timeout) {
        window.clearTimeout(timeout);
      }

      timeout = window.setTimeout(() => {
        const latestItem = pendingData.reduce((prev, current) =>
          prev.createdAt.getTime() > current.createdAt.getTime() ? prev : current,
        );
        triggerObserver({ item: latestItem });

        pendingData = [];
      }, 300);
    }
  };

  const observerCallback = (entries: IntersectionObserverEntry[]) => {
    entries.filter((entry) => entry.isIntersecting).map((entry) => checkTarget(entry.target));

    checkAndProcessPending();
  };

  const initObserver = () => {
    observer = new IntersectionObserver(observerCallback, { root: element.value, threshold: 0.2 });
  };

  const resetObserver = () => {
    observer?.disconnect();
    if (timeout) {
      window.clearTimeout(timeout);
    }
  };

  return { onObserver, getItemAttributes, setItemRef, initObserver, resetObserver };
};

export { useMessagesListIntersectionObserver };
